import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { IoEye, IoEyeOff } from 'react-icons/io5'

// Reusing original styles
import '../Make/SubprogramDetail.css'

import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import renderImage from '../../source/importImg'
import renderPrgImage from '../../source/programImg'
import getBackendImage from '../../source/imageUtil'
import JsonSidebar from './JsonSidebar'
import {
    exportScreenJson,
    validateScreenJson,
    prepareImportJson,
} from './screenJsonPortability'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const SCREEN_TYPES = [
    'playdynamex',
    'assembly',
    'hex',
    'simulate',
    'play',
    'integrate',
    'micropython',
    'c',
]

const DISABLED_SCREEN_TYPES = []

const SCREEN_NAME_MAP = {
    playdynamex: 'Mechanical',
    assembly: 'Electronic',
    hex: 'Coding',
    simulate: 'Simulate',
    play: 'Play',
    integrate: 'Integrate',
    micropython: 'MicroPython',
    c: 'C',
}

const ADMIN_ROUTE_MAP = {
    hex: 'hexAdmin',
    micropython: 'microPythonAdmin',
    c: 'cAdmin',
}

/* ── Reusable inline‑style objects ── */
const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
}
const modalStyle = {
    background: '#fff',
    borderRadius: '14px',
    padding: '28px 32px',
    minWidth: '340px',
    maxWidth: '540px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
}
const listItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    marginBottom: '6px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    background: '#fafafa',
    userSelect: 'none',
    transition: 'background .15s, transform .1s',
}
const pillStyle = {
    background: '#e8ecf1',
    borderRadius: '6px',
    padding: '8px 12px',
    fontWeight: 600,
    fontSize: '0.95rem',
    flex: 1,
    textTransform: 'capitalize',
    cursor: 'pointer',
    color: '#333',
}
const iconBtnStyle = (color) => ({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.15rem',
    color,
    padding: '2px 6px',
    lineHeight: 1,
})

/* ── EditScreensPopup component ── */
function EditScreensPopup({
    screens,
    subId,
    onSave,
    onClose,
    onScreenClick,
    screensSaved,
    setScreensSaved,
    onScreenContentImported,
    mainPlayScreenId,
    onMainPlayChange,
}) {
    const [items, setItems] = useState(screens.map((s) => ({ ...s })))
    const [showAdd, setShowAdd] = useState(false)
    const [adding, setAdding] = useState(false)

    // Per-screen JSON download / upload
    const device = sessionStorage.getItem('allowedDevice') || 'Toki'
    const [busyId, setBusyId] = useState(null) // screenId currently downloading/uploading

    // Hex final-message inline editor
    const [hexEditId, setHexEditId] = useState(null) // screenId of hex being edited
    const [hexMessages, setHexMessages] = useState({})
    const [hexSaving, setHexSaving] = useState(false)

    const openHexEdit = async (scr) => {
        setHexEditId(scr.id)
        try {
            const res = await fetch(
                `${BACKEND_URL}/api/staging/hex/${subId}/${scr.id}`,
                { credentials: 'include' }
            )
            const data = await res.json()
            setHexMessages({
                finalStepTitle:       data.finalStepTitle       || 'Excellent!',
                finalStepInstruction: data.finalStepInstruction || 'Excellent! You have successfully built the logic.',
                uploadStepTitle:      data.uploadStepTitle      || 'Upload Code',
                uploadStepInstruction:data.uploadStepInstruction|| 'Upload the code to the device to see it in action!',
            })
        } catch { setHexMessages({}) }
    }

    const saveHexMessages = async (scr) => {
        setHexSaving(true)
        try {
            const current = await fetch(
                `${BACKEND_URL}/api/staging/hex/${subId}/${scr.id}`,
                { credentials: 'include' }
            ).then(r => r.json())
            await fetch(
                `${BACKEND_URL}/api/staging/hex/${subId}/${scr.id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ ...current, ...hexMessages }),
                }
            )
            setHexEditId(null)
        } catch (err) {
            window.alert('Failed to save: ' + err.message)
        }
        setHexSaving(false)
    }
    const fileInputRef = React.useRef(null)
    const pendingUploadRef = React.useRef(null) // screen awaiting a file selection

    /* Download: fetch staging content, strip non-portable fields, save as .json */
    const handleDownload = async (scr) => {
        if (!scr.id) {
            window.alert(
                'This screen has no saved content yet. Save screens first.'
            )
            return
        }
        setBusyId(scr.id)
        try {
            const res = await fetch(
                `${BACKEND_URL}/api/staging/${scr.type}/${subId}/${scr.id}`,
                { credentials: 'include' }
            )
            if (!res.ok) throw new Error(`Server returned ${res.status}`)
            const raw = await res.json()
            const cleaned = exportScreenJson(scr.type, raw)

            const blob = new Blob([JSON.stringify(cleaned, null, 2)], {
                type: 'application/json',
            })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${subId}-${scr.id}.json`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Failed to download screen JSON:', err)
            window.alert(`Failed to download screen JSON: ${err.message}`)
        }
        setBusyId(null)
    }

    /* Upload step 1: warn about overwrite, then open the file picker */
    const triggerUpload = (scr) => {
        if (!scr.id) {
            window.alert(
                'This screen has no saved content yet. Save screens first.'
            )
            return
        }
        const label = SCREEN_NAME_MAP[scr.type] || scr.type
        const ok = window.confirm(
            `⚠️ Uploading a JSON file will OVERWRITE all existing content for the "${label}" screen (ID: ${scr.id}).\n\nThis action cannot be undone. Do you want to continue?`
        )
        if (!ok) return
        pendingUploadRef.current = scr
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
            fileInputRef.current.click()
        }
    }

    /* Upload step 2: parse, validate, transform, save to staging */
    const handleFileSelected = async (e) => {
        const file = e.target.files && e.target.files[0]
        const scr = pendingUploadRef.current
        pendingUploadRef.current = null
        if (!file || !scr) return

        const label = SCREEN_NAME_MAP[scr.type] || scr.type
        setBusyId(scr.id)
        try {
            const text = await file.text()

            let parsed
            try {
                parsed = JSON.parse(text)
            } catch (parseErr) {
                window.alert('Invalid JSON file — it could not be parsed.')
                setBusyId(null)
                return
            }

            const { valid, error } = validateScreenJson(scr.type, parsed)
            if (!valid) {
                window.alert(
                    `This file does not match the expected "${label}" screen structure.\n\nProblem: ${error}\n\nUpload rejected.`
                )
                setBusyId(null)
                return
            }

            const payload = prepareImportJson(scr.type, parsed, {
                projectId: subId,
                device,
            })

            const res = await fetch(
                `${BACKEND_URL}/api/staging/${scr.type}/${subId}/${scr.id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                }
            )
            if (!res.ok) throw new Error(`Server returned ${res.status}`)

            if (typeof onScreenContentImported === 'function') {
                onScreenContentImported(scr)
            }
            window.alert(
                `✅ "${label}" screen (ID: ${scr.id}) was updated successfully from the uploaded file.`
            )
        } catch (err) {
            console.error('Failed to upload screen JSON:', err)
            window.alert(`Failed to upload screen JSON: ${err.message}`)
        }
        setBusyId(null)
    }

    const handleDelete = async (idx) => {
        const scr = items[idx]
        if (
            !window.confirm(
                `Remove "${scr.id || scr.type}" screen? This will also delete the associated files.`
            )
        )
            return

        // Delete the screen file from the backend
        if (scr.id) {
            try {
                await fetch(
                    `${BACKEND_URL}/api/project/${subId}/screen/${scr.type}/${scr.id}`,
                    {
                        method: 'DELETE',
                        credentials: 'include', // <--- ADDED THIS
                    }
                )
            } catch (err) {
                console.error('Failed to delete screen file:', err)
            }
        }

        setItems(items.filter((_, i) => i !== idx))
        setScreensSaved(false)
    }

    const handleAdd = async (type) => {
        setAdding(true)

        // --- NEW: Calculate the next safe ID based on the highest existing number ---
        let maxNum = 0
        items.forEach((item) => {
            if (item.type === type && item.id) {
                // Extract the number from the ID (e.g. "play2" -> 2)
                const num = parseInt(item.id.replace(type, ''), 10)
                if (!isNaN(num) && num > maxNum) {
                    maxNum = num
                }
            }
        })
        const requestedId = `${type}${maxNum > 0 ? maxNum + 1 : 1}`

        try {
            const res = await fetch(
                `${BACKEND_URL}/api/project/${subId}/screen`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ type, requestedId }), // <--- Send requestedId
                }
            )

            if (!res.ok) {
                const errText = await res.text()
                throw new Error(`Backend error: ${res.status} - ${errText}`)
            }

            const data = await res.json()

            if (data.screenId) {
                setItems([...items, { type, id: data.screenId }])
                setScreensSaved(false)
            } else {
                alert(
                    'Failed to create screen file on backend. No screenId returned.'
                )
            }
        } catch (err) {
            console.error('Failed to create screen:', err)
            alert(`Error creating screen: ${err.message}`)
        }
        setAdding(false)
        setShowAdd(false)
    }

    /* move up / down helpers */
    const moveUp = (idx) => {
        if (idx === 0) return
        const next = [...items]
        ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
        setItems(next)
        setScreensSaved(false)
    }
    const moveDown = (idx) => {
        if (idx === items.length - 1) return
        const next = [...items]
        ;[next[idx + 1], next[idx]] = [next[idx], next[idx + 1]]
        setItems(next)
        setScreensSaved(false)
    }

    /* Toggle a screen's visibility to the end user (subprogram runner).
       Hidden screens are skipped in the runner but stay editable here and
       remain fetchable by id (e.g. Play still reads its Assembly screen). */
    const toggleHidden = (idx) => {
        const next = items.map((it, i) =>
            i === idx ? { ...it, hidden: !it.hidden } : it
        )
        setItems(next)
        setScreensSaved(false)
    }

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 18px', fontSize: '1.15rem' }}>
                    Edit Screens
                </h3>

                {/* Hidden input shared by all per-screen Upload buttons */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    style={{ display: 'none' }}
                    onChange={handleFileSelected}
                />

                {items.length === 0 && (
                    <p
                        style={{
                            color: '#999',
                            textAlign: 'center',
                            margin: '20px 0',
                        }}
                    >
                        No screens yet. Click + to add one.
                    </p>
                )}

                <div>
                    {items.map((scr, idx) => (
                        <React.Fragment key={`${scr.id || scr.type}-${idx}`}>
                        <div
                            style={{
                                ...listItemStyle,
                                ...(scr.hidden && {
                                    background: '#fff4e0',
                                    border: '1px dashed #e0b070',
                                }),
                            }}
                        >
                            {/* Clickable Pill for Navigation */}
                            <div
                                style={{
                                    ...pillStyle,
                                    // Visual feedback: If not saved, make it look "locked"
                                    opacity: screensSaved ? 1 : 0.6,
                                    cursor: screensSaved
                                        ? 'pointer'
                                        : 'not-allowed',
                                    border: screensSaved
                                        ? 'none'
                                        : '1px dashed #999',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                                onClick={() =>
                                    onScreenClick(scr.type, idx, scr.id)
                                }
                                disabled={!screensSaved}
                                title={`Edit ${scr.id || scr.type} screen details`}
                                onMouseEnter={(e) =>
                                    (e.target.style.background = '#d0d7e1')
                                }
                                onMouseLeave={(e) =>
                                    (e.target.style.background = '#e8ecf1')
                                }
                            >
                                <span>{SCREEN_NAME_MAP[scr.type]}</span>
                                <span
                                    style={{
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        color: '#555',
                                        marginLeft: '6px',
                                    }}
                                >
                                    {scr.id ? `ID: ${scr.id}` : ''}
                                </span>
                                {/* <span
                                    style={{
                                        fontSize: '0.7rem',
                                        color: '#888',
                                        marginLeft: '8px',
                                    }}>
                                    ({scr.id})
                                </span> */}
                                {scr.hidden && (
                                    <span
                                        style={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            color: '#b26a00',
                                            marginLeft: '6px',
                                        }}
                                    >
                                        Hidden
                                    </span>
                                )}
                                {!screensSaved && (
                                    <span
                                        style={{
                                            fontSize: '0.7rem',
                                            color: '#888',
                                        }}
                                    >
                                        Save to edit
                                    </span>
                                )}
                            </div>

                            {/* Hide / show screen from the end-user runner */}
                            <button
                                style={{
                                    ...iconBtnStyle(
                                        scr.hidden ? '#b26a00' : '#555'
                                    ),
                                    backgroundColor: 'rgb(0, 0, 0, 0.1)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                title={
                                    scr.hidden
                                        ? 'Screen is hidden from users — click to show'
                                        : 'Screen is visible to users — click to hide'
                                }
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleHidden(idx)
                                }}
                            >
                                {scr.hidden ? <IoEyeOff /> : <IoEye />}
                            </button>

                            {/* Main play screen selector — only shown for play-type screens */}
                            {scr.type === 'play' && (
                                <button
                                    style={{
                                        ...iconBtnStyle(
                                            mainPlayScreenId === scr.id ? '#f59e0b' : '#aaa'
                                        ),
                                        backgroundColor: mainPlayScreenId === scr.id
                                            ? 'rgba(245,158,11,0.12)'
                                            : 'rgb(0,0,0,0.07)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1rem',
                                    }}
                                    title={
                                        mainPlayScreenId === scr.id
                                            ? 'This is the main Play screen (shown when user clicks Play)'
                                            : 'Set as main Play screen'
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onMainPlayChange(
                                            mainPlayScreenId === scr.id ? null : scr.id
                                        )
                                    }}
                                >
                                    {mainPlayScreenId === scr.id ? '★' : '☆'}
                                </button>
                            )}

                            {/* Download / Upload screen JSON */}
                            <button
                                style={{
                                    ...iconBtnStyle('#1a73e8'),
                                    opacity: busyId === scr.id ? 0.4 : 1,
                                    cursor:
                                        busyId === scr.id
                                            ? 'wait'
                                            : 'pointer',
                                    backgroundColor: 'rgb(0, 0, 0, 0.1)',
                                    borderRadius: '50%',
                                }}
                                title="Download JSON"
                                disabled={busyId === scr.id}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleDownload(scr)
                                }}
                            >
                                ⬇
                            </button>
                            <button
                                style={{
                                    ...iconBtnStyle('#28A745'),
                                    opacity: busyId === scr.id ? 0.4 : 1,
                                    cursor:
                                        busyId === scr.id
                                            ? 'wait'
                                            : 'pointer',
                                    backgroundColor: 'rgb(0, 0, 0, 0.1)',
                                    borderRadius: '50%',
                                }}
                                title="Upload JSON (overwrites this screen)"
                                disabled={busyId === scr.id}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    triggerUpload(scr)
                                }}
                            >
                                ⬆
                            </button>

                            {/* Move arrows for reordering */}
                            <button
                                style={iconBtnStyle('#555')}
                                title="Move up"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setScreensSaved(false)
                                    moveUp(idx)
                                }}
                            >
                                ▲
                            </button>
                            <button
                                style={iconBtnStyle('#555')}
                                title="Move down"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setScreensSaved(false)
                                    moveDown(idx)
                                }}
                            >
                                ▼
                            </button>

                            {/* Edit completion messages (hex only) */}
                            {scr.type === 'hex' && (
                                <button
                                    style={{
                                        ...iconBtnStyle('#6200ea'),
                                        backgroundColor: hexEditId === scr.id ? 'rgba(98,0,234,0.12)' : 'rgb(0,0,0,0.07)',
                                        borderRadius: '50%',
                                    }}
                                    title="Edit completion messages"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (hexEditId === scr.id) setHexEditId(null)
                                        else openHexEdit(scr)
                                    }}
                                >
                                    ✎
                                </button>
                            )}

                            {/* Delete */}
                            <button
                                style={iconBtnStyle('#DC3545')}
                                title="Remove"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setScreensSaved(false)
                                    handleDelete(idx)
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Hex inline message editor */}
                        {scr.type === 'hex' && hexEditId === scr.id && (
                            <div style={{ padding: '12px', background: '#f5f0ff', borderTop: '1px solid #c4b5fd', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {[
                                    { key: 'finalStepTitle', label: 'Completion title (step 18)' },
                                    { key: 'finalStepInstruction', label: 'Completion message (step 18)' },
                                    { key: 'uploadStepTitle', label: 'Upload step title (step 19)' },
                                    { key: 'uploadStepInstruction', label: 'Upload step message (step 19)' },
                                ].map(({ key, label }) => (
                                    <div key={key}>
                                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4a0080', display: 'block', marginBottom: '3px' }}>{label}</label>
                                        <input
                                            type="text"
                                            value={hexMessages[key] || ''}
                                            onChange={(e) => setHexMessages(prev => ({ ...prev, [key]: e.target.value }))}
                                            style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #c4b5fd', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                ))}
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                                    <button onClick={() => setHexEditId(null)} style={{ padding: '5px 12px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer', background: '#fff', fontSize: '0.82rem' }}>Cancel</button>
                                    <button
                                        onClick={() => saveHexMessages(scr)}
                                        disabled={hexSaving}
                                        style={{ padding: '5px 12px', borderRadius: '4px', border: 'none', cursor: 'pointer', background: '#6200ea', color: '#fff', fontSize: '0.82rem', fontWeight: 600 }}
                                    >
                                        {hexSaving ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Add new screen */}
                {showAdd ? (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginTop: '12px',
                            padding: '10px',
                            background: '#f5f5f5',
                            borderRadius: '8px',
                        }}
                    >
                        {SCREEN_TYPES.map((type) => {
                            const isDisabledType =
                                DISABLED_SCREEN_TYPES.includes(type)
                            return (
                                <button
                                    key={type}
                                    onClick={() =>
                                        !isDisabledType && handleAdd(type)
                                    }
                                    disabled={adding || isDisabledType}
                                    title={
                                        isDisabledType
                                            ? 'Coming soon'
                                            : undefined
                                    }
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: '6px',
                                        border: '1px solid #ccc',
                                        background: isDisabledType
                                            ? '#f0f0f0'
                                            : '#fff',
                                        cursor: isDisabledType
                                            ? 'not-allowed'
                                            : adding
                                              ? 'wait'
                                              : 'pointer',
                                        fontWeight: 500,
                                        textTransform: 'capitalize',
                                        color: isDisabledType
                                            ? '#aaa'
                                            : 'inherit',
                                        opacity:
                                            adding && !isDisabledType ? 0.6 : 1,
                                    }}
                                >
                                    {SCREEN_NAME_MAP[type] || type}
                                    {isDisabledType ? ' (soon)' : ''}
                                </button>
                            )
                        })}
                        <button
                            onClick={() => setShowAdd(false)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                                background: '#eee',
                                cursor: 'pointer',
                                fontWeight: 500,
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => {
                            setShowAdd(true)
                            setScreensSaved(false)
                        }}
                        style={{
                            marginTop: '12px',
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: '2px dashed #999',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            color: '#555',
                            fontWeight: 600,
                        }}
                    >
                        + Add Screen
                    </button>
                )}

                {/* Footer buttons */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        marginTop: '22px',
                    }}
                >
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 22px',
                            borderRadius: '8px',
                            border: '1px solid #ccc',
                            background: '#f5f5f5',
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSave(items)}
                        style={{
                            padding: '8px 22px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#007BFF',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    )
}

/* ── Main Component ── */
const SubprogramDetailAdmin = (props) => {
    const history = useHistory()
    const { subId } = useParams()
    const device = sessionStorage.getItem('allowedDevice') || 'Toki'

    const [program, setProgram] = useState(null)
    const [subprogram, setSubprogram] = useState(null)

    // Admin editing states
    const [isEditing, setIsEditing] = useState(false)
    const [editDescription, setEditDescription] = useState('')
    const [editLink, setEditLink] = useState('') // <-- NEW STATE FOR LINK
    const [showScreensPopup, setShowScreensPopup] = useState(false)
    const [screensSaved, setScreensSaved] = useState(true)

    const [isStarting, setIsStarting] = useState(false)
    const [isPublishing, setIsPublishing] = useState(false)
    const [showJsonSidebar, setShowJsonSidebar] = useState(false)
    // Bumped after a screen's content is imported so the JSON inspector remounts
    // and re-fetches fresh content instead of showing its cached copy.
    const [jsonRefreshKey, setJsonRefreshKey] = useState(0)

    useEffect(() => {
        fetch(`${BACKEND_URL}/api/program/staging/${device.toLowerCase()}`, { credentials: 'include' })
            .then(async (res) => {
                if (!res.ok) {
                    console.warn(
                        `Staging program for "${device}" returned ${res.status}.`
                    )
                    return { subprograms: [] }
                }
                return res.json()
            })
            .then((data) => {
                const safeData =
                    data && Array.isArray(data.subprograms)
                        ? data
                        : { ...(data || {}), subprograms: [] }
                setProgram(safeData)
                const sub = safeData.subprograms.find((s) => s.id === subId)
                if (sub) {
                    setSubprogram(sub)
                    setEditDescription(sub.description || '')
                    setEditLink(sub.link || '') // <-- INITIALIZE LINK
                }
            })
            .catch((err) => {
                console.error('Failed to load staging program:', err)
            })
    }, [subId])

    const goBack = () => {
        history.push('/makeAdmin')
    }

    const handleScreenClick = (screenType, index, screenId) => {
        if (!screensSaved) {
            alert("Please click 'Save' before editing individual screens.")
            return
        }
        setScreensSaved(true)
        // Map screen type to the admin route name
        const adminRoute =
            ADMIN_ROUTE_MAP[screenType] ||
            `${screenType.charAt(0).toUpperCase() + screenType.slice(1)}Admin`
        if (screenId) {
            history.push(`/makeAdmin/${subId}/${adminRoute}/${screenId}`)
        } else {
            history.push(`/makeAdmin/${subId}/${adminRoute}`)
        }
    }

    /* Generic helper – saves the whole program blob back to staging */
    const saveProgramToBackend = useCallback(
        async (updatedProgram) => {
            try {
                await fetch(`${BACKEND_URL}/api/program/staging/${device.toLowerCase()}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedProgram),
                    credentials: 'include',
                })
                setProgram(updatedProgram)
                // refresh local subprogram reference
                const refreshed = updatedProgram.subprograms.find(
                    (s) => s.id === subId
                )
                if (refreshed) {
                    setSubprogram(refreshed)
                    setEditDescription(refreshed.description)
                    setEditLink(refreshed.link || '') // <-- REFRESH LINK
                }
            } catch (err) {
                console.error('Failed to save:', err)
                alert('Failed to save changes.')
            }
        },
        [subId]
    )

    const handleSaveDescription = async () => {
        if (!program || !subprogram) return
        const updatedSubprograms = program.subprograms.map((s) =>
            // <-- ADD link: editLink BELOW description
            s.id === subId
                ? { ...s, description: editDescription, link: editLink }
                : s
        )
        await saveProgramToBackend({
            ...program,
            subprograms: updatedSubprograms,
        })
        setIsEditing(false)
    }

    const handleMainPlayChange = async (screenId) => {
        if (!program || !subprogram) return
        const updatedSubprograms = program.subprograms.map((s) =>
            s.id === subId ? { ...s, playScreenId: screenId || null } : s
        )
        await saveProgramToBackend({ ...program, subprograms: updatedSubprograms })
    }

    const handleSaveScreens = async (newScreens) => {
        setScreensSaved(true) // Mark as saved to allow navigation from pills
        if (!program || !subprogram) return

        // --- Tell backend to ensure files exist for these screens ---
        try {
            await fetch(`${BACKEND_URL}/api/project/${subId}/screens`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                // Send the full screen objects with type and id
                body: JSON.stringify({
                    screens: newScreens.map((s) => ({
                        type: s.type,
                        id: s.id,
                    })),
                }),
            })
        } catch (err) {
            console.error('Failed to generate backend files for screens:', err)
            alert('Warning: Could not create backend files for new screens.')
        }

        const updatedSubprograms = program.subprograms.map((s) =>
            s.id === subId ? { ...s, screens: newScreens } : s
        )
        await saveProgramToBackend({
            ...program,
            subprograms: updatedSubprograms,
        })
        setShowScreensPopup(false)
    }

    const handleStart = (subId) => {
        if (isStarting) return
        setIsStarting(true)

        // Preview runs the admin's UNPUBLISHED (staging) content directly, so
        // there's no need to publish to live first or check sync status.
        if (!subprogram.screens || subprogram.screens.length === 0) {
            window.alert(
                'This subprogram has no screens to display. Add some by clicking "Edit Screens".'
            )
            setIsStarting(false)
            return
        }

        console.log('Starting staging preview for subprogram:', subId)
        history.push(`/makeAdmin/${subId}/preview`)
    }

    // Publishes ONLY this subprogram (staging → live), leaving every other
    // subprogram's live content untouched. Mirrors the validation flow of the
    // bulk "Publish to Live" button on MakeAdmin, but scoped to this subId.
    const handlePublishSubprogram = async () => {
        if (isPublishing || !subprogram) return

        // Step 1: Require a custom image.
        if (!subprogram.image || subprogram.image === 'projectDefault') {
            window.alert(
                'Cannot publish! This subprogram is missing a custom image.\n\nPlease set an image on the admin courses page (via "Change Image") before publishing.'
            )
            return
        }

        // Step 2: Require at least one screen.
        if (!subprogram.screens || subprogram.screens.length === 0) {
            window.alert(
                'Cannot publish! This subprogram has no screens. Add some via "Edit Screens" first.'
            )
            return
        }

        setIsPublishing(true)

        // Commented out - Allowing no audio for this
        // Step 3: Check audio files for this subprogram's screens only.
        // try {
        //     const audioRes = await fetch(
        //         `${BACKEND_URL}/api/check-audio?programId=${device.toLowerCase()}&subId=${encodeURIComponent(
        //             subId
        //         )}`,
        //         { credentials: 'include' }
        //     )
        //     const audioData = await audioRes.json()

        //     if (!audioData.ok) {
        //         const lines = audioData.missing.map(
        //             (m) =>
        //                 `• ${SCREEN_NAME_MAP[m.screenType] || m.screenType} (ID: ${m.screenId})`
        //         )
        //         window.alert(
        //             `Cannot publish! The following screens are missing audio files:\n\n${lines.join('\n')}\n\nPlease generate voiceovers for these screens before publishing.`
        //         )
        //         setIsPublishing(false)
        //         return
        //     }
        // } catch (err) {
        //     console.error('Audio check failed:', err)
        //     alert('Failed to check audio files. Please try again.')
        //     setIsPublishing(false)
        //     return
        // }

        // Step 4: Final confirmation & publish.
        const confirmPublish = window.confirm(
            `Are you sure you want to publish "${subprogram.title}" to live?\n\nThis will overwrite this subprogram's live content for regular users. Other subprograms are not affected.`
        )
        if (!confirmPublish) {
            setIsPublishing(false)
            return
        }

        try {
            const response = await fetch(
                `${BACKEND_URL}/api/publish-staging/${device.toLowerCase()}/${encodeURIComponent(
                    subId
                )}`,
                {
                    method: 'POST',
                    credentials: 'include',
                }
            )

            if (response.ok) {
                alert(
                    `Success! "${subprogram.title}" is now live.`
                )
            } else {
                const data = await response.json().catch(() => ({}))
                alert(
                    `Error: ${data.error || 'Failed to publish this subprogram.'}`
                )
            }
        } catch (err) {
            console.error('Publish failed:', err)
            alert('A network error occurred while publishing.')
        } finally {
            setIsPublishing(false)
        }
    }

    if (!subprogram) {
        return (
            <div className="SubDetail_Main">
                <div className="Select_Headers">
                    <MakeHeader
                        title="Admin: Course Details"
                        isGoBack={true}
                        goBack={goBack}
                    />
                </div>
                <div
                    className="SubDetail_Body"
                    style={{ justifyContent: 'center', alignItems: 'center' }}
                >
                    <div style={{ fontSize: '1.2vw', color: '#888' }}>
                        Loading...
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="SubDetail_Main">
            <div className="Select_Headers">
                <MakeHeader
                    title={`Admin Editing: ${subprogram.title}`}
                    titlePosition="left"
                    isGoBack={true}
                    goBack={goBack}
                />
            </div>

            <div className="SubDetail_Body">
                <div className="SubDetail_Left">
                    <img
                        src={getBackendImage(
                            subprogram.image || 'projectDefault'
                        )}
                        alt={subprogram.title}
                        className="SubDetail_Image"
                    />
                </div>

                <div className="SubDetail_Right">
                    <div className="SubDetail_Title">{subprogram.title}</div>

                    <div
                        className="SubDetail_Description"
                        style={{ position: 'relative' }}
                    >
                        {isEditing ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                }}
                            >
                                <textarea
                                    value={editDescription}
                                    onChange={(e) =>
                                        setEditDescription(e.target.value)
                                    }
                                    style={{
                                        width: '100%',
                                        minHeight: '150px',
                                        padding: '10px',
                                        fontSize: '1rem',
                                        borderRadius: '5px',
                                        border: '1px solid #ccc',
                                        fontFamily: 'inherit',
                                    }}
                                />
                                {/* <-- NEW LINK INPUT FIELD --> */}
                                <input
                                    type="text"
                                    value={editLink}
                                    onChange={(e) =>
                                        setEditLink(e.target.value)
                                    }
                                    placeholder="Enter link (optional)"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        fontSize: '1rem',
                                        borderRadius: '5px',
                                        border: '1px solid #ccc',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>
                        ) : (
                            <>
                                {(subprogram.description || '')
                                    .split('\n')
                                    .map((line, i) => (
                                        <p key={i}>{line}</p>
                                    ))}

                                {/* <-- NEW RENDER LINK IF IT EXISTS --> */}
                                {subprogram.link && (
                                    <a
                                        href={subprogram.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="SubDetail_Link"
                                    >
                                        Visit Source Link
                                    </a>
                                )}
                            </>
                        )}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            marginTop: '20px',
                            flexWrap: 'wrap',
                            maxWidth: '500px',
                        }}
                    >
                        {isEditing ? (
                            <>
                                <button
                                    className="SubDetail_Start"
                                    onClick={handleSaveDescription}
                                    style={{ background: '#28A745' }}
                                >
                                    Save Changes
                                </button>
                                <button
                                    className="SubDetail_Start"
                                    onClick={() => {
                                        setIsEditing(false)
                                        setEditDescription(
                                            subprogram.description
                                        ) // Revert
                                    }}
                                    style={{ background: '#6C757D' }}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="SubDetail_Options"
                                    onClick={() => setIsEditing(true)}
                                    style={{ background: '#454545' }}
                                >
                                    <img
                                        src={renderImage('edit')}
                                        alt="Edit Icon"
                                        style={{
                                            marginRight: '20px',
                                            width: '16px',
                                        }}
                                    />
                                    Edit Description
                                </button>
                                <button
                                    className="SubDetail_Options"
                                    onClick={() => setShowScreensPopup(true)}
                                    style={{ background: '#454545' }}
                                >
                                    <img
                                        src={renderImage('ActionCopyBtn')}
                                        alt="Edit Icon"
                                        style={{
                                            marginRight: '20px',
                                            width: '32px',
                                        }}
                                    />
                                    Edit Screens
                                </button>
                                <button
                                    className="SubDetail_Start"
                                    onClick={() => handleStart(subId)}
                                    style={{
                                        opacity: isStarting ? 0.7 : 1,
                                        cursor: isStarting ? 'wait' : 'pointer',
                                    }}
                                    disabled={isStarting}
                                >
                                    {isStarting ? 'Opening…' : 'Start Preview'}
                                </button>
                                <button
                                    className="SubDetail_Start"
                                    onClick={handlePublishSubprogram}
                                    disabled={isPublishing}
                                    style={{
                                        background: '#ff4b4b',
                                        opacity: isPublishing ? 0.7 : 1,
                                        cursor: isPublishing
                                            ? 'wait'
                                            : 'pointer',
                                    }}
                                    title="Publish only this subprogram to live"
                                >
                                    {isPublishing
                                        ? 'Publishing…'
                                        : 'Publish to Live'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Dev JSON buttons – right side */}
            <div
                style={{
                    position: 'fixed',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    zIndex: 100,
                }}
            >
                <button
                    onClick={() => setShowJsonSidebar(true)}
                    title="View raw JSON data for screens"
                    style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #555',
                        background: '#2d2d30',
                        color: '#d4d4d4',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontFamily: "'Menlo', 'Consolas', monospace",
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                >
                    {'{ }'} JSON
                </button>
            </div>

            {/* JSON Sidebar */}
            {showJsonSidebar && (
                <JsonSidebar
                    key={jsonRefreshKey}
                    subprogram={subprogram}
                    program={program}
                    onClose={() => setShowJsonSidebar(false)}
                />
            )}

            {/* Screens Editor Popup */}
            {showScreensPopup && (
                <EditScreensPopup
                    screens={subprogram.screens || []}
                    subId={subId}
                    onSave={handleSaveScreens}
                    onClose={() => setShowScreensPopup(false)}
                    onScreenClick={handleScreenClick}
                    screensSaved={screensSaved}
                    setScreensSaved={setScreensSaved}
                    onScreenContentImported={() =>
                        setJsonRefreshKey((k) => k + 1)
                    }
                    mainPlayScreenId={subprogram.playScreenId || null}
                    onMainPlayChange={handleMainPlayChange}
                />
            )}
        </div>
    )
}

const mapStateToProps = (state) => ({
    languageName: state.device.language,
})

export default connect(mapStateToProps)(SubprogramDetailAdmin)
